/**
 * Replicates the original wowslider.js ws_bulframe thumbnail-preview feature.
 * When the HTL-rendered bullets contain thumbnail <img> elements, a floating
 * preview strip (.ws_bulframe) is built above the bullet row and shown on hover,
 * scrolling to display the relevant thumbnail — identical to the jQuery original.
 */
export function initBulletPreview(
  bulletsInner: HTMLElement,
  thumbImgs: HTMLImageElement[]
): void {
  // Swiper has already replaced bullet content; read its generated <a> elements
  const bullets = Array.from(
    bulletsInner.querySelectorAll<HTMLAnchorElement>("a")
  );
  if (!bullets.length) return;

  // Structure mirrors the original:
  //   .ws_bulframe
  //     div            ← viewport  (overflow:hidden, fixed width via CSS)
  //       div          ← strip     (width = (n+1)*100%, contains all thumbs)
  //     span           ← arrow notch
  const bulframe = document.createElement("div");
  bulframe.className = "ws_bulframe";
  // Start hidden+transparent so the first show can skip the animation
  bulframe.style.display = "none";
  bulframe.style.opacity = "0";

  const viewport = document.createElement("div");
  const strip = document.createElement("div");

  // Derive thumbnail dimensions from the first image's natural size so the
  // frame always fits exactly one thumbnail, regardless of the CSS 308px default.
  const thumbH = 90;
  const firstImg = thumbImgs[0];
  const aspectRatio =
    firstImg.naturalWidth && firstImg.naturalHeight
      ? firstImg.naturalWidth / firstImg.naturalHeight
      : 16 / 9; // fallback if images not yet decoded
  const thumbW = Math.round(thumbH * aspectRatio);

  // Place all images side-by-side in the strip using absolute pixel widths
  thumbImgs.forEach((img) => {
    img.style.display = "block";
    img.style.float = "left";
    img.style.width = `${thumbW}px`;
    img.style.height = `${thumbH}px`;
    img.style.objectFit = "cover";
    strip.appendChild(img);
  });

  // Explicit inline sizes override the CSS width:308px on .ws_bulframe div
  strip.style.position = "relative";
  strip.style.width = `${thumbImgs.length * thumbW}px`;
  strip.style.height = `${thumbH}px`;
  viewport.style.overflow = "hidden";
  viewport.style.position = "relative";
  viewport.style.width = `${thumbW}px`;
  viewport.style.height = `${thumbH}px`;

  viewport.appendChild(strip);
  bulframe.appendChild(viewport);
  bulframe.appendChild(document.createElement("span"));
  bulletsInner.appendChild(bulframe);

  let hideTimer: ReturnType<typeof setTimeout> | null = null;
  // activeIndex < 0 means "never shown yet" → skip animation on first display
  let activeIndex = -1;

  const showPreview = (index: number): void => {
    if (index < 0) index = 0;
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }

    // Update ws_overbull active class
    if (activeIndex >= 0) bullets[activeIndex]?.classList.remove("ws_overbull");
    bullets[index]?.classList.add("ws_overbull");

    const bullet = bullets[index];
    const img = thumbImgs[index];
    const firstTop = bullets[0]?.offsetTop ?? 0;
    const lastTop = bullets[bullets.length - 1]?.offsetTop ?? 0;

    // Horizontal position: align right edge of bulframe near the hovered bullet
    const frameLeft = bullet.offsetLeft - bulframe.offsetWidth + 10;
    // Vertical margin offsets (from original formula)
    const marginTop = `${bullet.offsetTop - firstTop}px`;
    const marginBottom = `${lastTop - bullet.offsetTop}px`;
    // Scroll strip so the correct thumbnail is centred in the viewport
    const stripLeft = -img.offsetLeft + (img.offsetWidth - img.clientWidth) / 2;

    if (activeIndex < 0) {
      // First ever show — no animation, just snap into position (mirrors jQuery W.css(ad))
      bulframe.style.transition = "none";
      strip.style.transition = "none";
      bulframe.style.left = `${frameLeft}px`;
      bulframe.style.marginTop = marginTop;
      bulframe.style.marginBottom = marginBottom;
      strip.style.left = `${stripLeft}px`;
      bulframe.style.display = "block";
      bulframe.style.opacity = "1";
    } else {
      // Subsequent shows — animate at jQuery "fast" speed (200 ms)
      // Make visible first, then force reflow so the CSS transition fires
      bulframe.style.display = "block";
      bulframe.getBoundingClientRect(); // trigger reflow
      bulframe.style.transition = "left 0.2s ease, opacity 0.2s ease";
      strip.style.transition = "left 0.2s ease";
      bulframe.style.opacity = "1";
      bulframe.style.left = `${frameLeft}px`;
      bulframe.style.marginTop = marginTop;
      bulframe.style.marginBottom = marginBottom;
      strip.style.left = `${stripLeft}px`;
    }

    activeIndex = index;
  };

  // Hover on individual bullets triggers preview for that index
  bullets.forEach((bullet, i) => {
    bullet.addEventListener("mouseenter", () => showPreview(i));
  });

  // Container mouseenter: cancel any pending hide and re-show at current index
  bulletsInner.addEventListener("mouseenter", () => {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    if (activeIndex >= 0) showPreview(activeIndex);
  });

  // Container mouseleave: fade out then hide (mirrors W.animate({opacity:0}, complete:W.hide))
  bulletsInner.addEventListener("mouseleave", () => {
    bullets.forEach((b) => b.classList.remove("ws_overbull"));
    bulframe.style.transition = "opacity 0.2s ease";
    bulframe.style.opacity = "0";
    hideTimer = setTimeout(() => {
      bulframe.style.display = "none";
      hideTimer = null;
    }, 200);
  });
}
